import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Target, Users, PhoneCall, Gauge, ThumbsUp, AlertTriangle, MapPin, CalendarClock, FileText, Mailbox, UserCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/app/PageHeader";
import { Loader, EmptyState } from "@/components/app/Loader";
import { StatCard } from "@/components/app/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const BAND_COLOR = { "Low (<40)": "#C0392B", "Mid (40-69)": "#B8860B", "High (70+)": "#1E7E34" };

async function downloadCut(params, name) {
  try {
    const r = await api.get("/voters/cut-list", { params, responseType: "blob" });
    const href = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = href;
    a.download = name;
    a.click();
    URL.revokeObjectURL(href);
    toast.success("GOTV walk sheet exported");
  } catch (e) {
    toast.error(apiError(e));
  }
}

function CoverageBar({ pct }) {
  const color = pct >= 60 ? "#1E7E34" : pct >= 25 ? "#B8860B" : "#C0392B";
  return (
    <div className="h-2.5 w-full rounded-full bg-slate-100">
      <div className="h-2.5 rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
    </div>
  );
}

function TurfAssign({ precinct, current }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(current);
  const save = async () => {
    if (value === current) return;
    try {
      await api.post("/voters/turfs", { precinct, assignee: value });
      toast.success(value ? `${precinct} assigned to ${value}` : `${precinct} unassigned`);
      qc.invalidateQueries({ queryKey: ["turfs"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-3 flex items-center gap-1.5">
      <UserCheck className="h-3.5 w-3.5 shrink-0 text-brand-mute" />
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
        placeholder="Assign a canvasser…"
        className="h-7 text-xs"
        data-testid={`turf-assign-${precinct}`}
      />
    </div>
  );
}

export default function GOTV() {
  const { data, isLoading } = useQuery({ queryKey: ["gotv"], queryFn: () => api.get("/voters/gotv-dashboard").then((r) => r.data) });
  const { data: turfs } = useQuery({ queryKey: ["turfs"], queryFn: () => api.get("/voters/turfs").then((r) => r.data) });
  const turfMap = Object.fromEntries((turfs || []).map((t) => [t.precinct, t.assignee]));

  if (isLoading) return <Loader label="Crunching turnout model + field data…" />;
  if (!data || !data.total) return <EmptyState icon={Target} title="No voter data" description="Import a voter file to build the GOTV model." action={<Button asChild className="bg-brand-red text-white"><Link to="/voters">Upload voter file</Link></Button>} />;

  const bands = Object.entries(data.turnout_bands).map(([name, value]) => ({ name, value }));
  const fieldTotal = Math.max(1, data.field.supportive + data.field.undecided + data.field.opposed + data.field.no_contact);

  return (
    <div>
      <PageHeader
        title="GOTV / Turnout"
        subtitle="Your get-out-the-vote universe: modeled supporters who still need a turnout push, tracked against real field contacts."
        icon={Target}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => downloadCut({ list_type: "walk", fmt: "pdf", universe: "gotv" }, "gotv-walk-sheet.pdf")} className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="gotv-walk-pdf-btn">
              <FileText className="mr-2 h-4 w-4" /> GOTV walk PDF
            </Button>
            <Button asChild variant="outline"><Link to="/canvassing">Log canvass results</Link></Button>
          </div>
        }
      />

      {/* E-Day countdown + turnout goal */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex items-center gap-4 overflow-hidden rounded-xl bg-brand-navy p-5 text-white shadow-sm" data-testid="eday-countdown">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white/10">
            <CalendarClock className="h-6 w-6" />
          </div>
          <div>
            <p className="font-heading text-4xl font-extrabold leading-none">{data.days_to_eday ?? "-"}</p>
            <p className="mt-1 text-sm font-medium text-white/80">days to {data.next_election_label || "Election Day"}</p>
            <p className="text-xs text-white/60">
              {(data.next_election_date || data.election_date) ? new Date((data.next_election_date || data.election_date) + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric", year: "numeric" }) : ""}
              {data.primary_date && data.next_election_label === "Primary" ? ` · General: ${new Date(data.election_date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-5 shadow-sm" data-testid="goal-tracker">
          <div className="flex items-center justify-between">
            <p className="font-heading font-bold text-brand-ink">GOTV contact goal</p>
            <span className="font-heading text-xl font-extrabold text-brand-red">{data.goal_progress}%</span>
          </div>
          <p className="mt-0.5 text-sm text-brand-mute">{data.gotv_contacted.toLocaleString()} of {data.gotv_goal.toLocaleString()} target contacts</p>
          <div className="mt-3 h-3 w-full rounded-full bg-slate-100">
            <div className="h-3 rounded-full bg-gradient-to-r from-brand-red to-brand-navyhover transition-all" style={{ width: `${Math.max(2, data.goal_progress)}%` }} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="gotv-universe" icon={Target} accent="red" label="GOTV universe" value={data.gotv_universe.toLocaleString()} hint="supporters, turnout < 60" />
        <StatCard testId="gotv-contacted" icon={PhoneCall} accent="blue" label="Contacted" value={data.gotv_contacted.toLocaleString()} hint="reached in the field" />
        <StatCard testId="gotv-coverage" icon={Gauge} accent="amber" label="Coverage" value={`${data.coverage_pct}%`} hint="of GOTV universe" />
        <StatCard testId="gotv-supporters" icon={Users} accent="green" label="Total supporters" value={data.supporters.toLocaleString()} hint="modeled support ≥ 60" />
      </div>

      {/* Coverage meter */}
      <div className="mt-6 rounded-xl border border-border bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold text-brand-ink">GOTV universe coverage</h3>
          <span className="font-heading text-2xl font-extrabold text-brand-ink">{data.coverage_pct}%</span>
        </div>
        <p className="mt-1 text-sm text-brand-mute">{data.gotv_contacted.toLocaleString()} of {data.gotv_universe.toLocaleString()} mobilization targets contacted</p>
        <div className="mt-3"><CoverageBar pct={data.coverage_pct} /></div>
      </div>

      {/* AV / early-vote chase */}
      {data.av_chase && data.av_chase.applications > 0 && (
        <div className="mt-6 rounded-xl border border-border bg-white p-6 shadow-sm" data-testid="av-chase-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Mailbox className="h-5 w-5 text-brand-navy" />
              <h3 className="font-heading text-lg font-bold text-brand-ink">Absentee ballot chase</h3>
            </div>
            <Button variant="outline" size="sm" onClick={() => downloadCut({ list_type: "call", fmt: "csv", universe: "av_chase" }, "av-chase-call-list.csv")} data-testid="av-chase-call-list-btn">
              <PhoneCall className="mr-1.5 h-3.5 w-3.5" /> AV chase call list
            </Button>
          </div>
          <p className="mt-1 text-sm text-brand-mute">Supporters holding an unreturned absentee ballot are your cheapest votes, chase them before chasing doors.</p>
          <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard testId="av-applications" icon={Mailbox} accent="blue" label="AV applications" value={data.av_chase.applications.toLocaleString()} hint="absentee list" />
            <StatCard testId="av-outstanding" icon={AlertTriangle} accent="red" label="Ballots outstanding" value={data.av_chase.outstanding.toLocaleString()} hint="supporters, sent · not returned" />
            <StatCard testId="av-returned" icon={ThumbsUp} accent="green" label="Ballots banked" value={data.av_chase.returned.toLocaleString()} hint="supporter ballots returned" />
            <StatCard testId="av-early-voted" icon={UserCheck} accent="amber" label="Voted early" value={data.av_chase.early_voted.toLocaleString()} hint="in-person early vote" />
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Turnout bands */}
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-brand-ink">Modeled turnout bands</h3>
          <p className="text-sm text-brand-mute">Propensity across the full voter universe</p>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bands} margin={{ top: 4, right: 8, left: -16, bottom: 4 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6C757D" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6C757D" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {bands.map((b) => <Cell key={b.name} fill={BAND_COLOR[b.name]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Field confirmation */}
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-brand-ink">Field confirmation</h3>
          <p className="text-sm text-brand-mute">What canvassers actually heard ({data.total_canvassed} contacts)</p>
          <div className="mt-4 space-y-3">
            {[
              { k: "supportive", label: "Supportive", color: "#1E7E34", icon: ThumbsUp },
              { k: "undecided", label: "Undecided", color: "#B8860B" },
              { k: "opposed", label: "Opposed", color: "#C0392B" },
              { k: "no_contact", label: "No contact / other", color: "#94A3B8" },
            ].map((row) => (
              <div key={row.k}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-medium text-brand-ink">{row.label}</span>
                  <span className="text-brand-mute">{data.field[row.k]}</span>
                </div>
                <div className="h-2.5 w-full rounded-full bg-slate-100">
                  <div className="h-2.5 rounded-full" style={{ width: `${(data.field[row.k] / fieldTotal) * 100}%`, background: row.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Priority precincts */}
      <div className="mt-6 rounded-xl border border-border bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-brand-red" />
          <h3 className="font-heading text-lg font-bold text-brand-ink">Where to knock next</h3>
        </div>
        <p className="mt-1 text-sm text-brand-mute">Highest-opportunity precincts with the least GOTV coverage so far.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.priority_precincts.map((p) => (
            <div key={p.precinct} className="rounded-lg border border-border p-4" data-testid={`priority-precinct-${p.precinct}`}>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-heading font-bold text-brand-ink"><MapPin className="h-4 w-4 text-brand-navy" /> {p.precinct}</span>
                <span className="text-sm font-semibold text-brand-mute">{p.coverage}%</span>
              </div>
              <p className="mt-1 text-xs text-brand-mute">{p.contacted} / {p.universe} targets contacted</p>
              <div className="mt-2"><CoverageBar pct={p.coverage} /></div>
              <TurfAssign precinct={p.precinct} current={turfMap[p.precinct] || ""} />
              <Button variant="outline" size="sm" className="mt-2 w-full hover:border-brand-red hover:text-brand-red"
                onClick={() => downloadCut({ list_type: "walk", fmt: "pdf", universe: "gotv", precinct: p.precinct }, `gotv-walk_${p.precinct}.pdf`)}
                data-testid={`priority-walk-pdf-${p.precinct}`}>
                <FileText className="mr-1.5 h-3.5 w-3.5" /> Build walk PDF
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Full precinct table */}
      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-white shadow-sm">
        <div className="border-b border-border px-5 py-3"><h3 className="font-heading text-sm font-bold text-brand-ink">GOTV universe by precinct</h3></div>
        <div className="max-h-80 overflow-y-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Precinct</TableHead><TableHead className="text-right">Universe</TableHead><TableHead className="text-right">Contacted</TableHead><TableHead className="w-40">Coverage</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.by_precinct.map((p) => (
                <TableRow key={p.precinct}>
                  <TableCell className="font-medium text-brand-ink">{p.precinct}</TableCell>
                  <TableCell className="text-right">{p.universe}</TableCell>
                  <TableCell className="text-right">{p.contacted}</TableCell>
                  <TableCell><div className="flex items-center gap-2"><CoverageBar pct={p.coverage} /><span className="w-10 text-right text-xs text-brand-mute">{p.coverage}%</span></div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
